export type LeadDatabaseContext={routing_status?:string|null;source_type?:string;matrix_source?:string;capture_method?:string;website_area?:string|null;lead_area?:string|null;caller_area?:string|null;commercial_status?:string|null;service_requested?:string|null;reported_website?:string|null;source_site_label?:string|null;mapping_reason?:string;call_outcome?:string;call_duration?:number|null;lead_intent?:string;intent_basis?:string;has_recording?:boolean;has_summary?:boolean;has_transcript?:boolean};

export type LeadDatabaseRow=LeadDatabaseContext&{record_number?:number;record_key:string;record_kind:'lead'|'intake';lead_id:number|null;public_id:string|null;intake_key:string|null;provider_event_id:string|null;received_at:string;occurred_at:string|null;property_id:number|null;property_name:string|null;domain:string|null;client_id:number|null;client_name:string|null;client_basis:string;full_name:string|null;phone:string|null;email:string|null;provider:string;acquisition_source:string;channel:string;status:string;qualification_status:string;is_qa:boolean;is_duplicate:boolean;mapping_status:string;processing_status:string;campaign_name:string|null;analysis_status:string;quality_category:string|null;quality_score:number|null;delivery_status:string;delivered_at:string|null;recorded_destination:string|null;configured_destination:string|null;destination_basis:string};

export type LeadDatabasePage={rows:LeadDatabaseRow[];total_count:number;limit:number;offset:number;can_view_destinations:boolean;generated_at:string;facets:{providers:string[];acquisitions:string[]}};

export type DatabaseFilters={matrix_source:string;capture_method:string;call_outcome:string;lead_intent:string;search:string;provider:string;acquisition:string;client_id:string;property_id:string;delivery_status:string;start_date:string;end_date:string;record_type:string;view:string};

export const emptyDatabaseFilters:DatabaseFilters={matrix_source:'',capture_method:'',call_outcome:'',lead_intent:'',search:'',provider:'',acquisition:'',client_id:'',property_id:'',delivery_status:'',start_date:'',end_date:'',record_type:'production',view:'all'};

export const deliveryLabels:Record<string,string>={sent:'Sent to recipient',acknowledged:'Acknowledged',internal_only:'Internal notification only',failed:'Delivery failed',queued:'Queued',no_record:'No delivery record'};

/** Where a lead came from (decision D8). Every record has matrix_source; a consolidated lead takes its first interaction's. */
export const matrixSourceLabels:Record<string,string>={paid_google:'Paid · Google Ads',paid_meta:'Paid · Meta Ads',paid_other:'Paid · Other',organic_gbp:'Organic · Business Profile',organic_website:'Organic · Website',not_tracked:'Not tracked'};

export const isMatrixSource=(value:unknown):value is string=>typeof value==='string'&&Object.keys(matrixSourceLabels).includes(value);

/** The lead source label; a record without matrix_source (sample data) falls back to its paid or organic flag. */
export const leadSourceLabel=(matrix?:string|null,type?:string|null)=>matrix?matrixSourceLabels[matrix]??humanize(matrix):type==='paid'?'Paid':type==='organic'?'Organic':'Not tracked';

export const captureLabels:Record<string,string>={phone_call:'Phone call',form_submission:'Form submission',text:'Text message',unknown:'Unknown'};

export const callOutcomeLabels:Record<string,string>={answered:'Answered',abandoned:'Abandoned · provider reported',missed:'Missed',unanswered:'Unanswered',voicemail:'Voicemail',in_progress:'In progress at last sync',unknown:'Not recorded',not_applicable:'Not a phone call'};

export const intentLabels:Record<string,string>={spam:'Spam · provider flag/tag',telemarketing:'Telemarketing · provider tag',likely_spam:'Spam · AI review',likely_telemarketing:'Telemarketing · AI review',potential_lead:'Legitimate lead · AI review',needs_review:'Needs review · AI',insufficient_information:'Unclear / insufficient information',unassessed:'Not assessed'};

export const captureMethod=(r:LeadDatabaseRow)=>r.capture_method||(['call','phone_call'].includes(r.channel)?'phone_call':['form','website_form','form_submission','general_form'].includes(r.channel)?'form_submission':r.channel==='text'?'text':'unknown');

export function callRecordingLink(value:unknown):string|null{try{if(typeof value!=='string')return null;const url=new URL(value);return url.protocol==='https:'&&url.hostname==='app.callrail.com'&&!url.username&&!url.password?url.href:null;}catch{return null;}}

export const recordNumberLabel=(r:Pick<LeadDatabaseRow,'record_number'|'lead_id'>)=>{const number=r.record_number??r.lead_id;return number==null?'—':`#${number}`;};

export const databaseColumns=[

 {key:'received_at',label:'Received · ET',sort:'received_at',width:150},

 {key:'full_name',label:'Lead / contact',sort:'full_name',width:200},

 {key:'client_name',label:'Client',sort:'client_name',width:190},

 {key:'configured_destination',label:'Route / destination',sort:'configured_destination',width:260},

 {key:'delivery_status',label:'Delivery evidence',sort:'delivery_status',width:180},

 {key:'matrix_source',label:'Lead source',sort:'matrix_source',width:200},

 {key:'provider',label:'Intake provider',sort:'provider',width:145},

 {key:'acquisition_source',label:'Acquisition',sort:'acquisition_source',width:150},

 {key:'domain',label:'Website',sort:'domain',width:210},
 {key:'reported_website',label:'Reported website (unverified)',sort:null,width:210},
 {key:'phone',label:'Phone',sort:null,width:170},
 {key:'email',label:'Email',sort:null,width:220},

 {key:'recorded_destination',label:'Recorded recipient(s)',sort:null,width:260},

 {key:'mapping_status',label:'Mapping',sort:'mapping_status',width:130},

 {key:'status',label:'Lead status',sort:'status',width:135},
 {key:'routing_status',label:'Coverage status',sort:null,width:175},

 {key:'quality_score',label:'AI quality',sort:'quality_score',width:145},

 {key:'channel',label:'Contact method',sort:'capture_method',width:150},

 {key:'campaign_name',label:'Campaign',sort:null,width:180},

 {key:'qualification_status',label:'Qualification',sort:null,width:150},

 {key:'call_outcome',label:'Call outcome',sort:'call_outcome',width:210},

 {key:'call_duration',label:'Call duration',sort:'call_duration',width:130},

 {key:'lead_intent',label:'Lead assessment',sort:'lead_intent',width:220},

 {key:'website_area',label:'Website service area',sort:'website_area',width:200},

 {key:'lead_area',label:'Submitted city / ZIP',sort:null,width:190},

 {key:'caller_area',label:'Caller city · CallRail',sort:null,width:190},

 {key:'commercial_status',label:'Client relationship',sort:null,width:150},

 {key:'source_site_label',label:'Source website / tracker label',sort:null,width:220},

 {key:'mapping_reason',label:'Mapping explanation',sort:null,width:270},

 {key:'service_requested',label:'Service requested',sort:null,width:190},

 {key:'flags',label:'Flags',sort:null,width:120},

] as const;

export type DatabaseColumnKey=typeof databaseColumns[number]['key'];

export const defaultDatabaseColumns:DatabaseColumnKey[]=['received_at','full_name','client_name','configured_destination','delivery_status','matrix_source','channel','domain'];

export const humanize=(value:string|null|undefined)=>value?value.replaceAll('_',' '):'—';

export function databaseCell(row:LeadDatabaseRow,key:DatabaseColumnKey):string{

 if(key==='routing_status')return row.routing_status||'—';

 if(key==='matrix_source')return leadSourceLabel(row.matrix_source,row.source_type);

 if(key==='channel')return captureLabels[captureMethod(row)]||'Unknown';

 if(key==='call_outcome')return callOutcomeLabels[row.call_outcome||'unknown']||humanize(row.call_outcome);

 if(key==='call_duration')return row.call_duration==null?'Not recorded':`${row.call_duration}s`;

 if(key==='lead_intent')return intentLabels[row.lead_intent||'unassessed']||humanize(row.lead_intent);

 if(key==='flags')return [row.is_qa?'QA':'',row.is_duplicate?'Duplicate':''].filter(Boolean).join(', ')||'—';

 if(key==='received_at')return new Intl.DateTimeFormat('en-US',{timeZone:'America/New_York',dateStyle:'short',timeStyle:'short'}).format(new Date(row.received_at));

 if(key==='delivery_status')return deliveryLabels[row.delivery_status]||humanize(row.delivery_status);

 if(key==='quality_score')return row.quality_score===null?humanize(row.analysis_status):`${row.quality_score}/100 · ${humanize(row.quality_category)}`;

 if(key==='domain')return row.domain||row.property_name||'Unmapped website';

 if(key==='client_name')return row.client_id==null?'No Client':row.client_name||'No Client';

 if(key==='configured_destination')return row.configured_destination||'Not recorded';

 if(key==='recorded_destination')return row.recorded_destination||'No recipient recorded';

 return humanize(String(row[key]??''));

}

export function databaseCsv(rows:LeadDatabaseRow[],columns:DatabaseColumnKey[]){

 const escape=(v:string)=>'"'+(/^[\s]*[=+\-@]|^[\t\r\n]/.test(v)?"'"+v:v).replaceAll('"','""')+'"';

 return [['Record ID','Record reference',...columns.map(k=>databaseColumns.find(c=>c.key===k)!.label)],...rows.map(r=>[recordNumberLabel(r),r.record_key,...columns.map(k=>databaseCell(r,k))])].map(row=>row.map(escape).join(',')).join('\r\n');

}

export function initialDatabaseFilters(search:string):DatabaseFilters{

 const params=new URLSearchParams(search),filters={...emptyDatabaseFilters};

 for(const key of Object.keys(filters) as (keyof DatabaseFilters)[])if(params.has('db_'+key))filters[key]=(params.get('db_'+key)||'').slice(0,200);

 if(!['all','no_client','unmapped','delivery_gaps','intake'].includes(filters.view))filters.view='all';

 if(!['production','all','qa','duplicates'].includes(filters.record_type))filters.record_type='production';

 return filters;

}

